import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { MATCH_FIELD, SearchResultItem } from 'models/Search';
import Tag from 'components/ListingPortfolioCard/components/Tag';
import ResolveImage from 'components/ResolveImage';
import { getImgUrl, RESIZED_WIDTH } from 'components/ResolveImage/utils';
import { TextHighlighter } from 'components/TextHighlighter';
import ResolvePopup from 'components/ResolvePopup';
import useIsTextOverflowing from 'hooks/useIsTextOverflowing';
import { COLOR_VARIANT } from 'models/Colors';
import TagIcon from 'icons/Tag.icon';
import UserIcon from 'icons/User.icon';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import {
  DEAL_STATUS_COLOR,
  DEAL_STATUS_LABEL,
  LISTING_STATUS_COLOR,
  LISTING_STATUS_LABEL,
} from 'data/dealStatus';
import { getAskingPrice } from 'utils';
import { getSearchSectionTitle } from '../../../utils';

export type SearchResultsTableData = Pick<
  SearchResultItem,
  | 'portfolioName'
  | 'stage'
  | 'listingStage'
  | 'listing'
  | 'location'
  | 'industry'
  | 'section'
>;

const SHARED_COLUMNS: Array<keyof SearchResultsTableData> = [
  'portfolioName',
  'stage',
  'listingStage',
  'listing',
  'location',
];

export const COLUMNS: Array<keyof SearchResultsTableData> = [
  ...SHARED_COLUMNS,
  'industry',
];

export const BEST_MATCH_COLUMNS: Array<keyof SearchResultsTableData> = [
  ...SHARED_COLUMNS,
  'section',
];

export const HEADING_TRANSLATIONS: Record<
  keyof SearchResultsTableData,
  string
> = {
  portfolioName: 'deal',
  stage: 'status',
  listingStage: 'listing',
  listing: 'price',
  location: 'location',
  industry: 'industry',
  section: 'section',
};

export const CUSTOM_COL_SIZES: Partial<
  Record<keyof SearchResultsTableData, number>
> = {
  portfolioName: 350,
  stage: 100,
  listingStage: 100,
  location: 100,
  industry: 250,
  section: 250,
};

export const SHARED_TABLE_PROPS = {
  columns: COLUMNS,
  headingTranslations: HEADING_TRANSLATIONS,
  customSizes: CUSTOM_COL_SIZES,
  isDragDisabled: true,
  isResizeDisabled: true,
};

export const SKELETON_ROWS = 3;

type CellProps = {
  deal: SearchResultItem;
  type: string;
};

const MATCH_REASON_CONFIG = {
  [MATCH_FIELD.LISTING_NAME]: { label: 'listing', Icon: TagIcon },
  [MATCH_FIELD.BROKER]: { label: 'broker', Icon: UserIcon },
};

const DealCell: React.FC<CellProps> = ({ deal }) => {
  const { listing, name, matchedField, matches } = deal;

  const { t } = useTranslation();

  const isNameMatch = matchedField === MATCH_FIELD.NAME;

  const matchReason = matches?.find(({ field }) => field === matchedField);

  const secondLine =
    matchReason && !isNameMatch && MATCH_REASON_CONFIG[matchedField];

  return (
    <div className="search-results-table__deal">
      <ResolveImage
        src={getImgUrl(listing?.gallery?.[0]?.url, RESIZED_WIDTH[100])}
      />

      <div className="search-results-table__deal-info">
        <TextHighlighter
          className="search-results-table__name"
          text={name}
          {...(isNameMatch && { highlights: matchReason?.highlights })}
        />

        {secondLine && (
          <div className="search-results-table__match-reason">
            <secondLine.Icon />
            <span>{t(secondLine.label)}:</span>

            <TextHighlighter
              className="search-results-table__match-reason-value"
              text={matchReason.value}
              highlights={matchReason.highlights}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export const StatusCell: React.FC<{ deal: SearchResultItem }> = ({ deal }) => {
  const { stage } = deal;

  const { t } = useTranslation();

  return (
    <Tag
      className="search-results-table__status-tag"
      variant={DEAL_STATUS_COLOR[stage]}
      content={t(DEAL_STATUS_LABEL[stage])}
    />
  );
};

const ListingStatusCell: React.FC<CellProps> = ({ deal }) => {
  const { listingStage, listing } = deal;

  const { t } = useTranslation();

  if (listing?.isWithdrawn)
    return (
      <Tag
        className="search-results-table__status-tag"
        variant={COLOR_VARIANT.RED_FULL}
        content={t('withdrawn')}
      />
    );

  return (
    <Tag
      className="search-results-table__status-tag"
      variant={LISTING_STATUS_COLOR[listingStage]}
      content={t(LISTING_STATUS_LABEL[listingStage])}
    />
  );
};

const PriceCell: React.FC<CellProps> = ({ deal }) => (
  <>{getAskingPrice(deal.listing) || '-'}</>
);

const LocationCell: React.FC<CellProps> = ({ deal }) => (
  <>{deal.location || '-'}</>
);

export const IndustryCell: React.FC<{ deal: SearchResultItem }> = ({
  deal,
}) => {
  const { industry } = deal;

  const { isOverflowing, spanRef } = useIsTextOverflowing();

  const content = (
    <span ref={spanRef} className="search-results-table__industry">
      {industry || '-'}
    </span>
  );

  if (!isOverflowing) return content;

  return (
    <ResolvePopup onHover popupContent={industry} preferUpwards>
      {content}
    </ResolvePopup>
  );
};

export const SectionCell: React.FC<{ deal: SearchResultItem }> = ({ deal }) => {
  const { isSuperAdmin } = useContext(CurrentUserContext);
  const { t } = useTranslation();

  return (
    <Tag
      variant={COLOR_VARIANT.SILVER}
      content={t(getSearchSectionTitle(deal.section, isSuperAdmin))}
    />
  );
};

export const getBodyContentGenerate = (): Record<
  keyof SearchResultsTableData,
  (cellProps: CellProps) => React.ReactNode
> => ({
  portfolioName: (cellProps) => <DealCell {...cellProps} />,
  stage: (cellProps) => <StatusCell {...cellProps} />,
  listingStage: (cellProps) => <ListingStatusCell {...cellProps} />,
  listing: (cellProps) => <PriceCell {...cellProps} />,
  location: (cellProps) => <LocationCell {...cellProps} />,
  industry: (cellProps) => <IndustryCell {...cellProps} />,
  section: (cellProps) => <SectionCell {...cellProps} />,
});
